import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import PropTypes from "prop-types";
import { ReactSVG } from "react-svg";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import SubNavigation from "../../common/sub-navigation";
import NewToolTip from "../../../../common/New-ToolTip";
import PrescriptionTabs from "./prescription-tabs";
import MyResults from "../other/my-results";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import {
  classificationResultCalculation,
  nasalLubricantsData,
  nasalSprayData,
  nasalDropsData,
  oralAntihistamineData,
  intranasalCorticosteroidsData,
  salineNasalRinsesData,
  intranasalCombinationData,
  oralDecongestantsData,
  intranasalDecongestantsData,
  tabs,
} from "./data";
import { updateClinicalService } from "../../../../../redux/modules/clinical-services/actions";
import { getValidStep } from "../../../text/data";

// Constants for step navigation
const TREATMENT_STEP = 5;
const NON_PHARMACOLOGICAL_SUBSTEP = 5;
const CARD_WIDTH_CLASS = "w-[460px]";

const ActiveCardOutline = ({ type }) => {
  let color;
  switch (type) {
    case "pink":
      color = "stroke-pink-200";
      break;
    case "orange":
      color = "stroke-orange-300";
      break;
    case "teal":
      color = "stroke-teal-200";
      break;
    case "green":
      color = "stroke-green-100";
      break;
    default:
      color = "stroke-green-100";
  }

  return (
  <svg
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 z-10 h-full w-full"
    viewBox="0 0 460 460"
    preserveAspectRatio="none"
  >
    <path
      className={`fill-none ${color}`}
      d="M 457 230 L 457 23 Q 457 3 437 3 L 23 3 Q 3 3 3 23 L 3 437 Q 3 457 23 457 L 230 457"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
    <path
      className={`fill-none ${color}`}
      d="M 230 457 L 437 457 Q 457 457 457 437 L 457 230"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="4 18"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
)};

ActiveCardOutline.propTypes = {
  type: PropTypes.oneOf(["pink", "orange", "teal", "green"]),
};

const emptyTabState = {
  flippedCards: [],
  completedFlippedCards: [],
  activeCardIndex: null,
  isReviewed: false,
  hasAutoScrolledToChevron: false,
};

const getCardsByTabName = (tabName) => {
  const tabDataMap = {
    nasalSalineSpray: nasalSprayData,
    oralAntihistamines: oralAntihistamineData,
    intranasalCorticosteroids: intranasalCorticosteroidsData,
    nasalDrops: nasalDropsData,
    nasalSalineRinse: salineNasalRinsesData,
    nasalLubricants: nasalLubricantsData,
    intranasalCombination: intranasalCombinationData,
    oralDecongestants: oralDecongestantsData,
    intranasalDecongestants: intranasalDecongestantsData,
    addOnOralAntihistamines: oralAntihistamineData,
  };

  return tabDataMap[tabName] || [];
};

const DrugEducation = () => {
  const { clinicalServiceObj, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const { serviceId } = useParams();
  const dispatch = useDispatch();

  const { currentStep, currentSubStep, treatment } =
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm || {};
  const { frequencyOfSymptoms, levelOfSeverity } = treatment || {};

  const [totalTabs, setTotalTabs] = useState([]);
  const [educationState, setEducationState] = useState({
    activeTabIndex: 0,
    tabs: [],
  });
  const [noPrescriptionPopup, setNoPrescriptionPopup] = useState(false);
  const [showNoPrescriptionCard, setShowNoPrescriptionCard] = useState(false);
  const { showReferral } =
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm || {};
  const [showRef] = useState(showReferral ?? false);
  const educationRef = useRef(null);
  const cardRefs = useRef({});

  const storageKey = useMemo(
    () => `drug-education-progress-${serviceId}`,
    [serviceId]
  );

  const activeTab = totalTabs[educationState.activeTabIndex] || null;
  const currentCards = getCardsByTabName(activeTab?.name);
  const currentTabState =
    educationState.tabs[educationState.activeTabIndex] || emptyTabState;
  const currentCompletedFlippedCards = Array.isArray(
    currentTabState.completedFlippedCards
  )
    ? currentTabState.completedFlippedCards
    : [];
  const reviewedTabs = totalTabs
    .filter((tab, index) => educationState.tabs[index]?.isReviewed)
    .map((tab) => tab.name);

  const allTabsReviewed =
    totalTabs.length === 0 ||
    (educationState.tabs.length === totalTabs.length &&
      educationState.tabs.every((tab) => tab.isReviewed));

  const allCardsFlippedInActiveTab =
    currentCards.length > 0 &&
    Array.from({ length: currentCards.length }, (_, index) =>
      currentCompletedFlippedCards.includes(index)
    ).every(Boolean);

  const showLeftChevron = totalTabs.length > 1;
  const showRightChevron = totalTabs.length > 1;
  const shouldAutoScrollToRightChevron =
    allCardsFlippedInActiveTab &&
    educationState.activeTabIndex < totalTabs.length - 1;

  const persistEducationState = useCallback(
    (nextState, tabsForState = totalTabs) => {
      if (!serviceId || tabsForState.length === 0) return;

      try {
        const payload = {
          activeTabName: tabsForState[nextState.activeTabIndex]?.name ?? null,
          tabs: tabsForState.map((tab, index) => ({
            name: tab.name,
            flippedCards: nextState.tabs[index]?.flippedCards || [],
            completedFlippedCards:
              nextState.tabs[index]?.completedFlippedCards || [],
            activeCardIndex:
              typeof nextState.tabs[index]?.activeCardIndex === "number"
                ? nextState.tabs[index].activeCardIndex
                : null,
            isReviewed: !!nextState.tabs[index]?.isReviewed,
            hasAutoScrolledToChevron:
              !!nextState.tabs[index]?.hasAutoScrolledToChevron,
          })),
        };
        localStorage.setItem(storageKey, JSON.stringify(payload));
      } catch {
        // no-op: keep UI functional if localStorage is unavailable
      }
    },
    [serviceId, storageKey, totalTabs]
  );

  const hydrateEducationState = useCallback(
    (tabsForState) => {
      const next = {
        activeTabIndex: 0,
        tabs: tabsForState.map(() => ({ ...emptyTabState })),
      };

      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return next;
        const parsed = JSON.parse(raw);

        const hydratedTabs = tabsForState.map((tab) => {
          const persistedTab = parsed?.tabs?.find((item) => item.name === tab.name);
          const cardCount = getCardsByTabName(tab.name).length;
          const flippedCards = Array.isArray(persistedTab?.flippedCards)
            ? persistedTab.flippedCards.filter(
                (index) => Number.isInteger(index) && index >= 0 && index < cardCount
              )
            : [];
          const completedFlippedCards = Array.isArray(
            persistedTab?.completedFlippedCards
          )
            ? persistedTab.completedFlippedCards.filter(
                (index) => Number.isInteger(index) && index >= 0 && index < cardCount
              )
            : [];

          const activeCardIndex =
            Number.isInteger(persistedTab?.activeCardIndex) &&
            persistedTab.activeCardIndex >= 0 &&
            persistedTab.activeCardIndex < cardCount
              ? persistedTab.activeCardIndex
              : null;

          return {
            flippedCards,
            completedFlippedCards,
            activeCardIndex,
            isReviewed: !!persistedTab?.isReviewed,
            hasAutoScrolledToChevron: !!persistedTab?.hasAutoScrolledToChevron,
          };
        });

        const persistedActiveTabName = parsed?.activeTabName;
        const persistedActiveTabIndex = tabsForState.findIndex(
          (tab) => tab.name === persistedActiveTabName
        );

        return {
          activeTabIndex: persistedActiveTabIndex >= 0 ? persistedActiveTabIndex : 0,
          tabs: hydratedTabs,
        };
      } catch {
        return next;
      }
    },
    [storageKey]
  );

  // Check if any prescriptions exist from Non-Pharmacological Treatment
  const checkForPrescriptions = useCallback(() => {
    const treatment = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment;
    
    if (!treatment) return false;

    // Define prescription arrays to check
    const prescriptionArrays = [
      treatment.nasalSalineRinsePrescriptions,
      treatment.nasalSalineSprayPrescriptions,
      treatment.nasalLubricantPrescriptions,
      treatment.firstLineTherapyPrescriptions,
      treatment.stepUpTherapyPrescriptions,
      treatment.alternativeFirstLineTherapyPrescriptions,
      treatment.addOnTherapyOralDecongestantsPrescriptions,
      treatment.addOnTherapyIntranasalDecongestantsPrescriptions,
      treatment.addOnTherapyOralAntihistaminesPrescriptions,
    ];

    // Check if any prescription array has items
    return prescriptionArrays.some(prescriptions => prescriptions?.length > 0);
  }, [clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment]);

  // Show empty-state card and popup when there are no prescriptions
  useEffect(() => {
    const treatment = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment;
    if (!treatment) return;
    const hasPrescriptions = checkForPrescriptions();
    setShowNoPrescriptionCard(!hasPrescriptions);
    setNoPrescriptionPopup(!hasPrescriptions);
  }, [clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment, checkForPrescriptions]);

  useEffect(() => {
    const treatment =
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment;

    if (!treatment) return;

    const result = classificationResultCalculation(
      frequencyOfSymptoms,
      levelOfSeverity
    );

    let emptyTabs = [];

    const addTab = (tabIndex, medicine) => {
      let finalTab = { ...tabs[tabIndex] };
      if (medicine) finalTab.medicine = medicine;
      emptyTabs.push(finalTab);
    };

    if (treatment.nasalSalineRinsePrescriptions?.length > 0) {
      addTab(0, treatment.nasalSalineRinsePrescriptions[0]);
    }

    if (treatment.nasalSalineSprayPrescriptions?.length > 0) {
      addTab(
        treatment.nasalSalineSprayPrescriptions[0].medicationName.includes(
          "spray"
        )
          ? 1
          : 2,
        treatment.nasalSalineSprayPrescriptions[0]
      );
    }

    if (treatment.nasalLubricantPrescriptions?.length > 0) {
      addTab(5, treatment.nasalLubricantPrescriptions[0]);
    }

    const firstLineTherapy = treatment.firstLineTherapyPrescriptions?.[0];
    const stepUpTherapy = treatment.stepUpTherapyPrescriptions?.[0];
    const alternativeTherapy =
      treatment.alternativeFirstLineTherapyPrescriptions?.[0];

    if (firstLineTherapy) {
      if (result === "Mild Intermittent") {
        addTab(3, firstLineTherapy);
      } else {
        addTab(4, firstLineTherapy);
      }
    }

    if (stepUpTherapy) {
      if (result === "Mild Intermittent") {
        addTab(4, stepUpTherapy);
      } else if (
        result === "Moderate to Severe Intermittent" ||
        result === "Moderate to Severe Persistent"
      ) {
        addTab(6, stepUpTherapy);
      }
    }

    if (alternativeTherapy && result === "Mild Intermittent") {
      addTab(6, alternativeTherapy);
    }

    if (treatment.addOnTherapyOralAntihistaminesPrescriptions?.length > 0) {
      addTab(7, treatment.addOnTherapyOralAntihistaminesPrescriptions[0]);
    }

    setTotalTabs(emptyTabs);
  }, [
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment,
    frequencyOfSymptoms,
    levelOfSeverity,
  ]);

  useEffect(() => {
    if (totalTabs.length === 0) {
      setEducationState({ activeTabIndex: 0, tabs: [] });
      return;
    }

    const hydrated = hydrateEducationState(totalTabs);
    setEducationState(hydrated);
  }, [totalTabs, hydrateEducationState]);

  useEffect(() => {
    if (totalTabs.length === 0 || educationState.tabs.length !== totalTabs.length) {
      return;
    }
    persistEducationState(educationState, totalTabs);
  }, [educationState, totalTabs, persistEducationState]);

  useEffect(() => {
    cardRefs.current = {};
  }, [educationState.activeTabIndex]);

  const updateCurrentTabState = (updater) => {
    setEducationState((prev) => {
      if (
        prev.activeTabIndex < 0 ||
        prev.activeTabIndex >= prev.tabs.length
      ) {
        return prev;
      }

      const nextTabs = [...prev.tabs];
      nextTabs[prev.activeTabIndex] = updater(
        nextTabs[prev.activeTabIndex] || { ...emptyTabState }
      );

      return {
        ...prev,
        tabs: nextTabs,
      };
    });
  };

  const handleFlipCard = (index, isIconClick = false) => {
    if (!activeTab || index < 0 || index >= currentCards.length) return;

    updateCurrentTabState((tabState) => {
      const flippedCards = Array.isArray(tabState.flippedCards)
        ? tabState.flippedCards
        : [];
      const completedFlippedCards = Array.isArray(tabState.completedFlippedCards)
        ? tabState.completedFlippedCards
        : [];
      const alreadyOpen = flippedCards.includes(index);
      const nextFlippedCards = isIconClick
        ? alreadyOpen
          ? []
          : [index]
        : alreadyOpen
          ? flippedCards.filter((cardIndex) => cardIndex !== index)
          : [...flippedCards, index];

      const nextActiveCardIndex = alreadyOpen ? null : index;
      const nextCompletedFlippedCards = completedFlippedCards.includes(index)
        ? completedFlippedCards
        : [...completedFlippedCards, index];

      // If all cards are flipped in this tab, mark the tab as reviewed
      const isNowReviewed =
        currentCards.length > 0 &&
        nextCompletedFlippedCards.length === currentCards.length;

      return {
        ...tabState,
        flippedCards: nextFlippedCards,
        completedFlippedCards: nextCompletedFlippedCards,
        activeCardIndex: nextActiveCardIndex,
        isReviewed: isNowReviewed,
      };
    });
  };

  useEffect(() => {
    if (!shouldAutoScrollToRightChevron || !educationRef.current) return;
    if (currentTabState.hasAutoScrolledToChevron) return;

    const lastIndex = currentCards.length - 1;
    const lastCardElement = cardRefs.current[lastIndex];
    const container = educationRef.current;

    if (!lastCardElement) return;

    const spacingForChevron = 72;
    const targetLeft = Math.max(
      0,
      Math.min(
        lastCardElement.offsetLeft +
          lastCardElement.offsetWidth -
          container.clientWidth +
          spacingForChevron,
        container.scrollWidth - container.clientWidth
      )
    );

    if (targetLeft > container.scrollLeft + 2) {
      container.scrollTo({ left: targetLeft, behavior: "smooth" });
    }

    updateCurrentTabState((tabState) => ({
      ...tabState,
      hasAutoScrolledToChevron: true,
    }));
  }, [
    shouldAutoScrollToRightChevron,
    currentCards.length,
    currentTabState.hasAutoScrolledToChevron,
  ]);

  const handleAdvanceToNextTab = () => {
    if (totalTabs.length < 2) return;

    setEducationState((prev) => {
      if (prev.activeTabIndex < 0 || prev.activeTabIndex >= prev.tabs.length) {
        return prev;
      }

      const nextTabs = [...prev.tabs];
      const current = nextTabs[prev.activeTabIndex] || { ...emptyTabState };
      const currentTabCards = getCardsByTabName(totalTabs[prev.activeTabIndex]?.name);
      const completedCards = Array.isArray(current.completedFlippedCards)
        ? current.completedFlippedCards
        : [];
      const isCurrentTabReviewedNow =
        currentTabCards.length > 0 && completedCards.length === currentTabCards.length;
      nextTabs[prev.activeTabIndex] = {
        ...current,
        isReviewed: current.isReviewed || isCurrentTabReviewedNow,
      };

      const nextIndex =
        prev.activeTabIndex < nextTabs.length - 1 ? prev.activeTabIndex + 1 : 0;

      return {
        activeTabIndex: nextIndex,
        tabs: nextTabs,
      };
    });

    educationRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const handleGoToPreviousTab = () => {
    if (totalTabs.length < 2) return;
    setEducationState((prev) => {
      const prevIndex =
        prev.activeTabIndex > 0 ? prev.activeTabIndex - 1 : prev.tabs.length - 1;
      return {
        ...prev,
        activeTabIndex: prevIndex,
      };
    });

    // reset scroll for the container
    educationRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const updateSteps = (nextStep, nextSubStep) => {
    const updatedSteps = {
      currentStep: nextStep,
      currentSubStep: nextSubStep,
      serviceSteps: clinicalServiceSteps,
      showReferral: showRef,
    };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  const onNext = () => {
    if (!allTabsReviewed) return;
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      1
    );
    if (nextStep <= clinicalServiceSteps.length) {
      updateSteps(nextStep, nextSubStep);
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    updateSteps(nextStep, nextSubStep);
  };

  const getCardColorBasedOnType = (type, flippedCard) => {
    switch (type) {
      case "pink":
        return flippedCard
          ? "bg-gray-20 border-[5px] border-gray-50"
          : "bg-gray-50 items-center justify-center";
      case "orange":
        return flippedCard
          ? "bg-gray-20 border-[5px] border-gray-50"
          : "bg-gray-50 items-center justify-center";
      case "teal":
        return flippedCard
          ? "bg-gray-20 border-[5px] border-teal-75"
          : "bg-teal-75 items-center justify-center";
      case "green":
        return flippedCard
          ? "bg-gray-20 border-[5px] border-green-100"
          : "bg-green-100 items-center justify-center";
      default:
        return flippedCard
          ? "bg-gray-20 border-[5px] border-green-100"
          : "bg-green-100 items-center justify-center";
    }
  };

  const renderTabContent = () => {
    const renderContent = (data, index) => (
      <div
        className={clsx(
          `${CARD_WIDTH_CLASS} flex-shrink-0 box-border`,
          currentTabState.activeCardIndex === index &&
            " rounded-[20px]"
        )}
        ref={(element) => {
          if (element) {
            cardRefs.current[index] = element;
          }
        }}
      >
        <div
          id="outer-div"
          className={clsx(
            "relative my-[25px] p-[20px]",
            currentTabState.activeCardIndex === index && "rounded-[20px]"
          )}
        >
          {currentTabState.activeCardIndex === index ? (
            <ActiveCardOutline type={activeTab.type} />
          ) : null}
          <div
            key={index}
            onClick={() => handleFlipCard(index)}
            className={clsx(
              "flex flex-col relative w-full rounded-[20px] cursor-pointer flex-shrink-0 box-border h-[420px]",
              currentTabState.activeCardIndex === index
                && "outline outline-[6px] outline-white rounded-[20px]",
              getCardColorBasedOnType(
                activeTab.type,
                currentTabState.flippedCards.includes(index)
              )
            )}
          >
            {currentTabState.flippedCards.includes(index) ? (
              <div className="flex flex-col ">
                <p
                  className={`py-[24px] text-body-xl text-teal-200 font-bold flex items-center justify-center `}
                >
                  {data.altTitle}
                </p>
                <div className="px-[12px]">
                  {data.descriptionPoints(activeTab.type)}
                </div>
              </div>
            ) : (
              <p className="text-teal-200 text-center font-semibold text-body-2xl">
                {data.title}
              </p>
            )}
            <ReactSVG
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click event
                handleFlipCard(index, true);
              }}
              src={"/icons/flip.svg"}
              className="w-[20px] h-[20px] absolute cursor-pointer bottom-[10px] right-[10px] z-10"
            />
          </div>
        </div>
      </div>
    );

    return currentCards.map(renderContent);
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-1 gap-4">
        <ChartNav />
        <Medications highlightAmrEducationMatches />
        <div className="flex flex-1 w-full h-[668px] overflow-y-auto">
          <div className="w-full gap-5">
            <div className="flex items-center justify-between mt-0.5 mb-0.5">
              <h4 className="text-xs-display font-bold text-teal-300">
                Education - Review Prescriptions
              </h4>
              <SubNavigation />
            </div>
            <div className="border-b-[7px] border-gray-20 mb-[10px]"></div>
            <div className="flex flex-col">
              <div className="flex justify-between items-center space-x-[100px] border-b-[4px] border-gray-100">
                <PrescriptionTabs
                  activeTab={activeTab}
                  setActiveTab={(tab) => {
                    const nextIndex = totalTabs.findIndex((item) => item.name === tab?.name);
                    if (nextIndex >= 0) {
                      setEducationState((prev) => ({ ...prev, activeTabIndex: nextIndex }));
                    }
                  }}
                  tabs={totalTabs}
                  completedTabs={reviewedTabs}
                  showBorder={false}
                />
                <MyResults
                  frequencyOfSymptoms={frequencyOfSymptoms}
                  levelOfSeverity={levelOfSeverity}
                />
              </div>
              <div className="flex items-center space-x-4">
                <ReactSVG src="/icons/rx-education.svg" />
                <h2 className="text-display-xs font-bold text-teal-300 py-6">
                  {activeTab?.isAddOn ? "Add On Therapy: " : ""}
                  {activeTab?.medicine?.medicationName}
                  {activeTab?.medicine?.sig && ` - ${activeTab?.medicine?.sig}`}
                </h2>
              </div>
              <div
                className="flex gap-6 overflow-x-auto overflow-y-hidden flex-nowrap"
                id="education"
              >
                  {showNoPrescriptionCard ? (
                  <div className="relative flex flex-col items-center justify-center w-[415px] h-[420px] rounded-[12px] bg-gray-20 border border-gray-100 p-4 mt-4">
                    <button
                      className="absolute left-3 top-3 m-0 h-10 w-10 rounded-full bg-REELTeal border-REELTeal hover:bg-teal-200 text-white inline-flex border transition-all duration-300 items-center justify-center"
                      onClick={() => setNoPrescriptionPopup(true)}
                      aria-label="Open no-prescription popup"
                      data-tooltip-id="no-prescription-tooltip"
                    >
                      <ChevronLeftIcon className="w-6 h-6 text-white" />
                    </button>
                    <NewToolTip
                      id="no-prescription-tooltip"
                      render={() => (
                        <div className="bg-white p-2 max-w-[220px] shadow-lg rounded">
                          <p className="text-sm text-gray-800">Open no prescription dialog</p>
                        </div>
                      )}
                    />
                    <ReactSVG
                      src="/icons/rx-noprescription-icon.svg"
                      className="w-full max-w-[300px] h-auto"
                    />
                  </div>
                ) : (
                    <div className="relative w-full overflow-hidden z-30 isolate">
                      <div
                        className="w-full flex gap-6 overflow-x-auto overflow-y-hidden scroll-smooth flex-nowrap pr-16 pl-16"
                        id="education"
                        ref={educationRef}
                      >
                        {renderTabContent()}
                      </div>

                      {showLeftChevron ? (
                        <button
                          type="button"
                          onClick={handleGoToPreviousTab}
                          className="absolute left-0 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-gray-20 rounded-full flex items-center justify-center shadow-md"
                          aria-label="Go to previous prescription"
                        >
                          <ChevronLeftIcon className="w-8 h-8 text-teal-300" />
                        </button>
                      ) : null}

                      {showRightChevron ? (
                        <button
                          type="button"
                          onClick={handleAdvanceToNextTab}
                          className="absolute right-0 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-gray-20 rounded-full flex items-center justify-center shadow-md"
                          aria-label="Advance to next prescription"
                        >
                          <ChevronRightIcon className="w-8 h-8 text-teal-300" />
                        </button>
                      ) : null}
                    </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onPrevious}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              !allTabsReviewed ? "opacity-40 pointer-events-none" : ""
            }`}
            onClick={onNext}
          />
        </div>
      </div>
      
      <ConfirmationModalNew
        type="primary"
        title="No Prescription Added"
        className="!min-w-[625px]"
        content={() => (
          <div className="flex flex-col gap-4">
            <p className="text-body-md text-gray-900">
              You have not added any prescriptions for this Minor Ailment assessment. If treatment is required, please add a prescription before completing the service.
            </p>
            <p className="text-body-md text-teal-300">
              The service may be completed with assessment and patient education only.
            </p>
          </div>
        )}
        confirmText="Add Prescription"
        cancelText="Continue Without Prescription"
        open={noPrescriptionPopup}
        handleClose={() => {
          setNoPrescriptionPopup(false);
        }}
        cancelAction={() => {
          setNoPrescriptionPopup(false);
          setShowNoPrescriptionCard(true);
        }}
        confirmAction={() => {
          setNoPrescriptionPopup(false);
          // Navigate to Non-Pharmacological treatment step to add prescription
          const updatedSteps = {
            currentStep: TREATMENT_STEP, // Treatment step
            currentSubStep: NON_PHARMACOLOGICAL_SUBSTEP, // Non-Pharmacological substep
            serviceSteps: clinicalServiceSteps,
            showReferral: showRef,
          };
          dispatch(updateClinicalService(serviceId, updatedSteps));
        }}
        onCross={() => {
          setNoPrescriptionPopup(false);
          setShowNoPrescriptionCard(true);
        }}
      />
    </div>
  );
};

export default DrugEducation;
